import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { JwtHelperService } from '@auth0/angular-jwt';
import { AuthData } from '@classes/auth/auth-data';
import { AuthResponse } from '@classes/auth/auth-response';
import { AuthUser } from '@classes/auth/auth-user';
import { AppUser, InfosUser } from '@classes/auth/infosUser';
import { NavLinks } from '@classes/navlinks/navlinks';
import { ForgotPassword, UserAdd } from '@classes/user/userAdd';
import { environment } from '@environments/environment';
import { faSitemap } from '@fortawesome/free-solid-svg-icons';
import { DataService } from '@services/data/data.service';
import * as CryptoJS from 'crypto-js';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { tap } from 'rxjs/operators';
import { IsAdminService } from '../is-admin/is-admin.service';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  faSitemap = faSitemap;
  authChanged = new Subject<boolean>();
  currentUSer$ = new BehaviorSubject<InfosUser>(null);

  private identityApiUrl: string = environment.identityApiUrl;
  private apiUrl: string = environment.apiUrl;

  private tokenKey: string = environment.tokenKey;
  private rfTokenKey: string = environment.rfTokenKey;
  private tokenExpKey: string = environment.tokenExpKey;
  private afterLoginUrl = '/choose-appli';

  actualUser: any = null;

  public get currentUserValue(): InfosUser {
    return this.currentUSer$.value;
  }

  constructor(
    private router: Router,
    private http: HttpClient,
    private jwtHelperSrv: JwtHelperService,
    private isAdminService: IsAdminService,
    private dataService: DataService
  ) {}

  login(authData: AuthData): void {
    const hashedPassword = this.cryptString(authData.password);
    const httpParams = new HttpParams()
      .append('username', authData.username)
      .append('password', hashedPassword)
      .append('grant_type', 'password')
      // .append('scope', 'api offline_access')
      .append('client_id', 'web');

    this.http
      .post<AuthResponse>(this.identityApiUrl + 'connect/token', httpParams)
      .subscribe((result) => {
        this.saveToken(
          result.access_token,
          result.refresh_token,
          result.expires_in
        );
        this.authChanged.next(true);
        this.isAdminService.ifIsAdmin(this.getDecodedToken().role);

        this.router.navigateByUrl(this.afterLoginUrl);
      });
  }

  addNewUser(user: UserAdd) {
    return this.http.post(`${environment.apiUrl}/users/new-user`, user);
  }

  /**
   * Called if user forgot his password
   * Send email with link to reset password
   * @param username: username from request form
   */
  requestPasswordReset(username: string): Observable<void> {
    const httpParams = new HttpParams().append('username', username);

    return this.http.post<void>(
      this.apiUrl + 'users/password-reset-request',
      httpParams
    );
  }

  /**
   * Called if welcome link is expired or manually ask for new email in user management page
   * Send email with welcome link
   * @param username: username from email link or users list
   */
  requestPasswordInit(username: ForgotPassword): Observable<void> {
    const httpParams = new HttpParams().append('username', username.username);

    return this.http.post<void>(
      `${environment.apiUrl}/users/password-reset-request`,
      httpParams
    );
  }

  updatePassword(resetData: {
    currentPassword: string;
    newPassword: string;
  }): Observable<string> {
    const hashedCurrentPassword = this.cryptString(resetData.currentPassword);
    const hashedNewPassword = this.cryptString(resetData.newPassword);

    const httpParams = new HttpParams()
      .append('current_password', hashedCurrentPassword)
      .append('new_password', hashedNewPassword);

    return this.http
      .post<string>(this.apiUrl + 'users/password', httpParams)
      .pipe(
        tap(() => {
          this.afterPwdReset(resetData.newPassword);
        })
      );
  }

  // resetPassword(userId: string, token: string, newPassword: string): Observable<void> {
  //   const hashedNewPassword = this.cryptString(newPassword);

  //   // Send data in body in place of http params to avoid mess in token serialisation due to special characters
  //   const body = {
  //     token,
  //     new_password: hashedNewPassword
  //   };
  //   return this.http.post<void>(`${this.apiUrl}/users/${userId}/password-reset`, body)
  //     .pipe(
  //       tap(() => {
  //         this.router.navigate(['../login']);
  //       })
  //     );
  // }

  resetPassword(
    userId: string,
    token: string,
    newPassword: string
  ): Observable<void> {
    const hashedNewPassword = this.cryptString(newPassword);

    // Send data in body in place of http params to avoid mess in token serialisation due to special characters
    const body = {
      token,
      new_password: hashedNewPassword,
    };
    return this.http
      .post<void>(`${this.apiUrl}/users/${userId}/password-reset`, body)
      .pipe(
        tap(() => {
          this.router.navigate(['../login']);
        })
      );
  }

  refreshToken(): Observable<AuthResponse> {
    const refreshToken = localStorage.getItem(this.rfTokenKey);
    const httpParams = new HttpParams()
      .append('grant_type', 'refresh_token')
      // .append('scope', 'api offline_access')
      .append('client_id', 'web')
      .append('refresh_token', refreshToken);

    return this.http
      .post<AuthResponse>(this.identityApiUrl + 'connect/token', httpParams)
      .pipe(
        tap((result) => {
          this.saveToken(
            result.access_token,
            result.refresh_token,
            result.expires_in
          );
          this.authChanged.next(true);
        })
      );
  }

  logout(prevUrl?: string): void {
    if (prevUrl) {
      this.afterLoginUrl = prevUrl;
    }

    localStorage.clear();
    this.authChanged.next(false);
    this.router.navigate(['/login']);
    this.dataService.appName.next(environment.appName);
    this.dataService.logoApp.next(faSitemap);
  }

  isAuth(): boolean {
    const decodedToken = this.getDecodedToken();

    if (!decodedToken || decodedToken.upd_pwd_req === true) {
      return false;
    }

    return !this.isTokenExpired();
  }

  getDecodedToken(): any {
    let token = this.jwtHelperSrv.tokenGetter();
    if (token) {
      token = token.toString();
      const userInfos: InfosUser = this.jwtHelperSrv.decodeToken(token);
      const userApplication = userInfos.application.toString().split(',');
      const userAppUpdate: AppUser[] = [];
      userApplication?.forEach((item) => {
        const app = item.split('$');

        const obj: AppUser = {
          applicationName: app[0],
          roleUser: app[1],
        };

        userAppUpdate.push(obj);
      });

      userInfos.application = userAppUpdate;

      return userInfos;
    }

    return null;
  }

  getUser(): AuthUser {
    const decodedToken = this.getDecodedToken();

    if (!decodedToken) {
      return null;
    }

    this.actualUser = new AuthUser(decodedToken.name, decodedToken.client);

    return this.actualUser;
  }

  isAuthorized(): boolean {
    const token = this.getDecodedToken();

    if (!token) {
      return false;
    }

    // authorised
    return true;
  }

  private saveToken(
    accessToken: string,
    refreshToken: string,
    deadline: number
  ): void {
    const tokenExpDate = new Date();
    tokenExpDate.setSeconds(tokenExpDate.getSeconds() + deadline);

    localStorage.setItem(this.tokenExpKey, tokenExpDate.toString());
    localStorage.setItem(this.tokenKey, accessToken);
    localStorage.setItem(this.rfTokenKey, refreshToken);
  }

  private afterPwdReset(newPassword: string): void {
    const decodedToken = this.getDecodedToken();

    if (decodedToken) {
      this.login({ username: decodedToken.name, password: newPassword });
    } else {
      this.logout();
    }
  }

  private isTokenExpired(): boolean {
    const tokenExpDate = new Date(localStorage.getItem(this.tokenExpKey));
    const now = new Date();
    return tokenExpDate < now;
  }

  cryptString(stringTocrypt: string): string {
    return CryptoJS.SHA256(stringTocrypt).toString();
  }

  changeClientUser(clientId) {
    this.actualUser.clientId = clientId;
  }

  getActualUser() {
    return this.actualUser;
  }
}
